
/* eslint-disable @typescript-eslint/camelcase */
import { Vue, Component, Watch } from "vue-property-decorator";
import AuthMixin from "../../mixins/auth.mixin";
import { apiHandler } from "@/services/api-handler";
import { AggregatedOrderResponse } from "shared/api/routes/order";
import { Api } from "shared/api";
import { IUser } from "shared/entities/user";
import { OrderByItemTypeAndPOSResponse } from "shared/entities/order";
import { AuthService } from "@/services/auth";

declare module "vue/types/vue" {
  interface Vue {
    user: IUser | null;
  }
}

@Component
export default class SalesApproval extends Vue.extend(AuthMixin) {
  faqPdfLink = "/public/pdfs/SALES FAQ_updated.pdf";
  weekOptions: { number: number; dateRange: string }[] = [];
  aggregatedOrders: AggregatedOrderResponse = [];
  selectedYear = 2025;
  selectedWeekNumber = 1;
  userLocations: number[] = [];
  selectedLocation: number | null = null;
  signature="";
  selectedLocationRoyaltyPercent = 0;
  selectedLocationAdvertisingPercent = 0;
  selectedLocationTechFee = 0;
  uniqueOrderCount = 0;
  isLoading= false;
  existingSignature: string | null = null;
  logData() {
    return ""; // Return an empty string so it doesn't affect the template
  }

  generateWeekOptions() {
  const weeksArray = [];
  const firstDayOfYear = new Date(this.selectedYear, 0, 1); // January 1st of the selected year
  const lastDayOfYear = new Date(this.selectedYear, 11, 31); // December 31st of the selected year

  // Determine the starting day of the first week (Monday of the first week)
  let currentStartDate = new Date(firstDayOfYear);
  if (currentStartDate.getDay() !== 1) { // Check if it's not Monday
    const offset = (currentStartDate.getDay() + 6) % 7; // Offset to move back to Monday
    currentStartDate.setDate(currentStartDate.getDate() - offset);
  }

  let weekNumber = 1;

  while (currentStartDate <= lastDayOfYear) {
    const startDate = new Date(currentStartDate);
    const endDate = new Date(currentStartDate);
    endDate.setDate(endDate.getDate() + 6); // Add 6 days to get the end of the week

    // Format the date range as "MM/DD - MM/DD"
    const dateRange = `${startDate.getMonth() + 1}/${startDate.getDate()} - ${endDate.getMonth() + 1}/${endDate.getDate()}`;

    // Add the week to the array
    weeksArray.push({ number: weekNumber, dateRange });

    // Move to the next week
    currentStartDate.setDate(currentStartDate.getDate() + 7);

    // Explicitly reassign currentStartDate for the linter
    currentStartDate = new Date(currentStartDate);

    weekNumber++;
  }

  this.weekOptions = weeksArray;
}



async fetchFaqPdfLink() {
    try {
      const pdfUrl = process.env.API_ROOT + Api.file.faq._fullPath; // Construct the URL
      const response = await fetch(pdfUrl, {
        method: "GET",
        headers: { token: AuthService.getToken() ?? "" }
      });

      if (!response.ok) {
        throw new Error(`Error fetching FAQ PDF: ${response.statusText}`);
      }

      const blob = await response.blob(); // Treat response as a blob
      const url = URL.createObjectURL(blob); // Create URL for the blob
      this.faqPdfLink = url;
    } catch (error) {
         // eslint-disable-next-line no-console
      console.error("Error fetching FAQ PDF:", error);
      this.faqPdfLink = "";
    }
  }

async fetchSignature() {
  if (this.selectedLocation && this.selectedWeekNumber) {
    try {
      const weekData = await this.fetchWeekByYearIdAndWeekNumberId(this.selectedYear, this.selectedWeekNumber);
      const response = await apiHandler(() => Api.weeklySalesData.signature[this.selectedLocation][weekData.id].Get());
      this.existingSignature = response.signature;
    } catch (error) {
      // Handle the error
       // eslint-disable-next-line no-console
      console.error("Error fetching signature:", error);
      this.existingSignature = null; // Reset the signature in case of error
    }
  }
}

  get salesBreakdown() {
    const totalSales = this.salesTotal();
    return [
      { name: "Main Sales", percentage: this.calculatePercentageForWeek("Main", totalSales, "itemType") },
      { name: "Sides", percentage: this.calculatePercentageForWeek("Sides", totalSales, "itemType") },
      { name: "Catering", percentage: this.calculatePercentageForWeek("Catering", totalSales, "itemType") },
      { name: "On-Line", percentage: this.calculatePercentageForWeek("Online", totalSales, "pointOfSale") },
      { name: "Mobile", percentage: this.calculatePercentageForWeek("Mobile", totalSales, "pointOfSale") },
      { name: "Door Dash", percentage: this.calculatePercentageForWeek("Doordash", totalSales, "pointOfSale") },
      { name: "Uber Eats", percentage: this.calculatePercentageForWeek("UberEats", totalSales, "pointOfSale") }

    ];
  }

  @Watch("selectedLocation")
  onSelectedLocationChanged(newValue: number, oldValue: number) {
    if (newValue !== oldValue && newValue !== null) {
      this.fetchLocationDetails(newValue);
    }
  }

  calculatePercentageForWeek(category: string, totalSales: number, categoryType: "itemType" | "pointOfSale"): number {
    const totalForCategory = this.aggregatedOrders.reduce((sum, day) => {
    return sum + (categoryType === "itemType" ?
        this.calculateTotalForType(category, day.data) :
        this.calculateTotalForPos(category, day.data));
    }, 0);
    return totalSales > 0 ? ((Math.max(0,totalForCategory) / totalSales) * 100).toFixed(2) : 0;
  }

  formatAmount(amount: number | string): string {
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) {
        return "$0.00";
    }
    // Format the number with commas and two decimal places
    return `$${numericAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
}

  formatDay(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  }

  calculateTotalForType(itemType: string, orders: OrderByItemTypeAndPOSResponse[]): number {
    return orders
        .filter(order => order.itemType === itemType)
        .reduce((total, order) => total + parseFloat(order.totalAmount), 0);
  }

  calculatePercentageForType(itemType: string, orders: OrderByItemTypeAndPOSResponse[], totalSales: number): number {
    const totalForType = this.calculateTotalForType(itemType, orders);
    return totalSales > 0 ? ((totalForType / totalSales) * 100).toFixed(2) : 0;
  }

  calculatePercentage(itemType: string, orders: OrderByItemTypeAndPOSResponse[]): number {
    const totalSales = this.calculateTotalOrdersAmount(orders);
    if (totalSales === 0) {
      return 0;
    }
    const totalForType = this.calculateTotalForType(itemType, orders);
    return parseFloat(((totalForType / totalSales) * 100).toFixed(2));
  }

  // Method for calculating total orders amount for a day or for the entire week
  calculateTotalOrdersAmount(orders?: OrderByItemTypeAndPOSResponse[]): number {
    if (orders) {
      // Calculate total for a specific day
      return orders.reduce((total, order) => total + parseFloat(order.totalAmount), 0);
    } else {
      // Calculate total for the entire week
      return this.aggregatedOrders.reduce((weekTotal, day) => {
        const dayTotal = day.data.reduce((daySum, order) => daySum + parseFloat(order.totalAmount), 0);
        return weekTotal + dayTotal;
      }, 0);
    }
  }

  salesTotal(): number {
    const doordash = Math.max(0, this.posTotal("Doordash"));
    const uber = Math.max(0, this.posTotal("UberEats"));
    const online = Math.max(0, this.posTotal("Online"));
    const mobile = Math.max(0, this.posTotal("Mobile"));
    const total = doordash + uber + online + mobile;
    return total;
  }

  posTotal(posType: string): number {
  return this.aggregatedOrders.reduce((totalSum, dailyData) => {
      const posTotal = dailyData.data.reduce((posSum: number, order: { pointOfSale: string; totalAmount: string; }) => {
          if (order.pointOfSale === posType) {
              const amount = parseFloat(order.totalAmount);
              return posSum + (isNaN(amount) ? 0 : amount);
          }
          return posSum;
      }, 0);
      return totalSum + posTotal;
    }, 0);
  }

  calculateTotalForPos(posType: string, orders: OrderByItemTypeAndPOSResponse[]): number {
    // eslint-disable-next-line no-console
    // console.log(`Checking ${posType} orders:`, orders);
  return orders
    .filter(order => {
      const isPosMatch = order.pointOfSale === posType;
      const isValidAmount = !isNaN(parseFloat(order.totalAmount));
        // eslint-disable-next-line no-console
    //   console.log(`Order: ${order.orderId}, POS: ${order.pointOfSale}, Amount: ${order.totalAmount}, Is Valid: ${isValidAmount}`);
      return isPosMatch && isValidAmount;
    })
    .reduce((total, order) => total + parseFloat(order.totalAmount), 0);
}


  calculatePercentageForPos(posType: string, orders: OrderByItemTypeAndPOSResponse[]): number {
    const totalSales = this.calculateTotalOrdersAmount(orders);
    const totalForPos = this.calculateTotalForPos(posType, orders);
    return totalSales > 0 ? ((totalForPos / totalSales) * 100).toFixed(2) : 0;
  }

 adjustDateToUTC(dateString, hours, minutes, seconds) {
      const date = new Date(dateString);
      // Adjust date and time according to parameters
      date.setUTCHours(hours, minutes, seconds, 0);
      return date.toISOString();
    }

async fetchWeekData() {
  this.isLoading = true; // Start loading
  if (!this.selectedLocation) {
     // eslint-disable-next-line no-console
    console.error("No location selected");
    this.isLoading = false; // Stop loading if no location is selected
    return;
  }
  try {
    const weekData = await apiHandler(() => Api.weeks[this.selectedYear][this.selectedWeekNumber].Get());
    // Adjust week_start to 5 AM UTC
    const weekStartUTC = this.adjustDateToUTC(weekData.week_start, 5, 0, 0);
    // Adjust week_end to the day after at 4:59:59 AM UTC
    const weekEnd = new Date(weekData.week_end);
    weekEnd.setUTCDate(weekEnd.getUTCDate() + 1); // Move to the day after
    const weekEndUTC = this.adjustDateToUTC(weekEnd, 4, 59, 59);
    await this.fetchOrders(weekStartUTC, weekEndUTC, this.selectedLocation);
    const uniqueOrderCountData = await apiHandler(() => Api.orders[weekStartUTC][weekEndUTC][this.selectedLocation].Get());

    if (this.aggregatedOrders.length === uniqueOrderCountData.length) {
      this.aggregatedOrders.forEach((dayOrder, index) => {
        dayOrder.noOfSales = uniqueOrderCountData[index].uniqueOrderCount;
      });
    } else {
         // eslint-disable-next-line no-console
      console.error("Mismatch in the number of days between aggregatedOrders and uniqueOrderCountData");
    }
    await this.fetchSignature();
} catch (error) {
     // eslint-disable-next-line no-console
    console.error("Error fetching week data:", error);
  } finally {
    this.isLoading = false; // Stop loading after fetch completes or fails

  }
  // Ensure the aggregatedOrders are updated properly
  this.aggregatedOrders = JSON.parse(JSON.stringify(this.aggregatedOrders));
}

async fetchOrders(startDate: Date, endDate: Date, location: number) {
  this.aggregatedOrders = []; // Clear previous orders data
  try {
    const requestBody = { startDate, endDate, location };
    const responseData = await apiHandler(() => Api.orders.Post(requestBody));
    // eslint-disable-next-line no-console
    // console.log("Fetched Orders Data:", responseData);

    this.aggregatedOrders = responseData;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error fetching orders:", error);
  }
}


async fetchLocationById(locationId: number | null) {
  if (locationId === null) {
    throw new Error("Location ID is null");
  }
  // Use apiHandler to get the location data directly
  try {
    const locationData = await apiHandler(() => Api.locations[locationId].Get());
    return locationData; // Assuming the API returns the full location object
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error fetching location by ID:", error);
    throw error; // Re-throw the error to handle it in the calling function
  }
}

async fetchWeekByYearIdAndWeekNumberId(yearId: number, weekNumberId: number) {
  try {
    // Use apiHandler to get the week data directly
    const weekData = await apiHandler(() => Api.weeks[yearId][weekNumberId].Get());
    return weekData; // Assuming the API returns the week data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error fetching week data:", error);
    return null; // Return null or handle the error as needed
  }
}

async submitSignature() {
  if (!this.signature) {
    alert("Please enter your name to sign.");
    return;
  }
  if (!this.selectedLocation) {
    alert("Please select a location.");
    return;
  }
  try {
    // Fetch the week data based on selected year and week number
    const weekData = await this.fetchWeekByYearIdAndWeekNumberId(this.selectedYear, this.selectedWeekNumber);
    if (!weekData) {
        // eslint-disable-next-line no-console
      console.error("Failed to fetch week data");
      alert("Failed to fetch week data. Please try again.");
      return;
    }
    const signedData = {
      location_id: this.selectedLocation,
      week_id: weekData.id,
      signature: this.signature
    };
    // Call the method to update the signature in the database
    await this.updateSignature(signedData);
     // Refetch the signature data after updating
     await this.fetchSignature();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in submitSignature:", error);
    alert("Error submitting signature. Please try again.");
  }
}

async updateSignature(signedData) {
  try {
    // Use apiHandler to make the POST request
    await apiHandler(() => Api.weeklySalesData.signature.Post(signedData));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error updating signature:", error);
    alert("Failed to update signature. Please try again.");
  }
}

calculateRoyaltyDue(): number {
    // Calculate the total for the entire week
    const weekTotalSales = this.calculateTotalOrdersAmount();
    return weekTotalSales * (this.selectedLocationRoyaltyPercent / 100);
  }

  calculateAdvertisingDue(): number {
    // Calculate the total for the entire week
    const weekTotalSales = this.calculateTotalOrdersAmount();
    return weekTotalSales * (this.selectedLocationAdvertisingPercent / 100);
  }

  calculateTotalDue(): number {
    return this.calculateRoyaltyDue() + this.calculateAdvertisingDue() + this.selectedLocationTechFee;
  }

  async fetchLocationDetails(locationId: number) {
  const locationData = await this.fetchLocationById(locationId);
  if (locationData) {
    this.selectedLocationRoyaltyPercent = locationData.percent;
    this.selectedLocationAdvertisingPercent = locationData.advertising;
    this.selectedLocationTechFee = locationData.tech;
    this.$forceUpdate();
  }
}

async mounted() {
  if (this.user?.id) {
    try {
      const locationIds = await apiHandler(() => Api.user[this.user.id].locations.Get());
      // Fetch full location objects and update userLocations
      this.userLocations = await Promise.all(locationIds.map(async (id) => {
        const location = await this.fetchLocationById(id);
        return { id, name: location.name }; // Assuming fetchLocationById returns the full location object
      }));

      // Set the default selected location
      if (this.userLocations.length > 0) {
        this.selectedLocation = this.userLocations[0].id;
      }
      if (this.selectedLocation) {
    await this.fetchLocationDetails(this.selectedLocation);
    this.generateWeekOptions();
    await this.fetchFaqPdfLink();
  }
    } catch (error) {
        // eslint-disable-next-line no-console
      console.error("Error fetching user locations:", error);
    }
  }
  if (this.selectedLocation) {
      await this.fetchLocationDetails(this.selectedLocation);
    }
}

  totalNoOfSales() {
    return this.aggregatedOrders.reduce((sum, order) => sum + order.noOfSales, 0);
  }

  totalMain() {
    return this.formatAmount(this.aggregatedOrders.reduce((sum, order) => sum + this.calculateTotalForType("Main", order.data), 0));
  }

  totalSides() {
    return this.formatAmount(this.aggregatedOrders.reduce((sum, order) => sum + this.calculateTotalForType("Sides", order.data), 0));
  }

  totalCatering() {
    return this.formatAmount(this.aggregatedOrders.reduce((sum, order) => sum + this.calculateTotalForType("Catering", order.data), 0));
  }

  totalDoorDash() {
    return this.formatAmount(this.aggregatedOrders.reduce((sum, order) => sum + this.calculateTotalForPos("Doordash", order.data), 0));
  }

  totalUber() {
    return this.formatAmount(this.aggregatedOrders.reduce((sum, order) => sum + this.calculateTotalForPos("UberEats", order.data), 0));
  }

  totalOnline() {
    return this.formatAmount(this.aggregatedOrders.reduce((sum, order) => sum + this.calculateTotalForPos("Online", order.data), 0));
  }

  totalMobile() {
    return this.formatAmount(this.aggregatedOrders.reduce((sum, order) => sum + this.calculateTotalForPos("Mobile", order.data), 0));
  }

  // Add a method to calculate the total voids/refunds for the entire week
  totalVoidsRefunds() {
  return this.formatAmount(this.aggregatedOrders.reduce((sum, order) => sum + this.calculateTotalForType("Cancelled Order", order.data), 0));
}

  totalRoyaltySales() {
    // Assuming calculateTotalOrdersAmount sums the "Royalty Sales" or adjust accordingly
    return this.formatAmount(this.calculateTotalOrdersAmount());
  }

  @Watch("selectedYear")
onSelectedYearChanged() {
  this.generateWeekOptions();
}

generatePrintableVersion() {
    window.print();
}

}
